/**
 * ChangeOrderDrawer Component
 * Create/Edit/View Change Order form
 * BuilderBel Construction Project Management System
 */

import { yupResolver } from '@hookform/resolvers/yup';
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  DollarSign,
  FileText,
  Plus,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useToast } from '../../contexts/ToastContext';
import { useCurrentProjectId } from '../../hooks/useProjectRedux';
import { useGetCostCodesQuery } from '../../services/costCodeApi';
import {
  useCreateChangeOrderMutation,
  useUpdateChangeOrderMutation,
} from '../../services/changeOrderApi';
import type { CostCode } from '../../types/costCode';
import type {
  ChangeOrder,
  ChangeOrderCostType,
  ChangeOrderPriority,
  ChangeOrderType,
  CreateChangeOrderRequest,
  UpdateChangeOrderRequest,
} from '../../types/changeOrder';
import {
  CurrencyInput,
  DatePicker,
  Dropdown,
  Input,
  NumberInput,
  SearchableDropdown,
  TextArea,
} from '../forms';
import { StatusBadge } from '../ui';

// Props interface
export interface ChangeOrderDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  changeOrder?: ChangeOrder | null;
  mode: 'create' | 'edit' | 'view';
  onSuccess?: () => void;
}

// Line item form data
interface LineItemFormData {
  description: string;
  costCode: string;
  costCodeDescription?: string;
  quantity: number;
  unit: string;
  unitCost: number;
  costType: ChangeOrderCostType;
}

// Markups form data
interface MarkupsFormData {
  overhead: number;
  profit: number;
  insurance: number;
  bond: number;
  contingency: number;
}

// Schedule impact form data
interface ScheduleImpactFormData {
  hasImpact: boolean;
  daysAdded: number;
  description: string;
}

// Form data type
interface ChangeOrderFormData {
  title: string;
  description: string;
  type: ChangeOrderType;
  priority: ChangeOrderPriority;
  lineItems: LineItemFormData[];
  markups: MarkupsFormData;
  taxRate: number;
  isCredit: boolean;
  scheduleImpact: ScheduleImpactFormData;
  requestDate: string;
  requiredByDate: string;
}

// Validation schema
const lineItemSchema = Yup.object({
  description: Yup.string().required('Description is required'),
  costCode: Yup.string().required('Cost code is required'),
  costCodeDescription: Yup.string().optional(),
  quantity: Yup.number().required('Quantity is required').min(0.01, 'Quantity must be positive'),
  unit: Yup.string().required('Unit is required'),
  unitCost: Yup.number().required('Unit cost is required').min(0, 'Unit cost must be non-negative'),
  costType: Yup.string()
    .oneOf(['labor', 'material', 'equipment', 'subcontractor', 'other'])
    .required('Cost type is required'),
});

const changeOrderSchema = Yup.object({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  type: Yup.string()
    .oneOf([
      'owner_requested',
      'unforeseen_condition',
      'design_error',
      'scope_clarification',
      'value_engineering',
      'allowance_adjustment',
    ])
    .required('Type is required'),
  priority: Yup.string()
    .oneOf(['low', 'medium', 'high', 'critical'])
    .required('Priority is required'),
  lineItems: Yup.array().of(lineItemSchema).min(1, 'At least one line item is required'),
  markups: Yup.object({
    overhead: Yup.number().min(0).max(100).optional(),
    profit: Yup.number().min(0).max(100).optional(),
    insurance: Yup.number().min(0).max(100).optional(),
    bond: Yup.number().min(0).max(100).optional(),
    contingency: Yup.number().min(0).max(100).optional(),
  }),
  taxRate: Yup.number().min(0).max(100).optional(),
  isCredit: Yup.boolean(),
  scheduleImpact: Yup.object({
    hasImpact: Yup.boolean(),
    daysAdded: Yup.number()
      .min(0)
      .when('hasImpact', {
        is: true,
        then: schema => schema.required('Days added is required when there is schedule impact'),
      }),
    description: Yup.string(),
  }),
  requestDate: Yup.string().required('Request date is required'),
  requiredByDate: Yup.string().optional(),
});

// Type options
const TYPE_OPTIONS = [
  { value: 'owner_requested', label: 'Owner Requested' },
  { value: 'unforeseen_condition', label: 'Unforeseen Condition' },
  { value: 'design_error', label: 'Design Error' },
  { value: 'scope_clarification', label: 'Scope Clarification' },
  { value: 'value_engineering', label: 'Value Engineering' },
  { value: 'allowance_adjustment', label: 'Allowance Adjustment' },
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

// Cost type options
const COST_TYPE_OPTIONS = [
  { value: 'labor', label: 'Labor' },
  { value: 'material', label: 'Material' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'other', label: 'Other' },
];

// Unit options
const UNIT_OPTIONS = [
  { value: 'each', label: 'Each' },
  { value: 'unit', label: 'Unit' },
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'sqft', label: 'Square Feet' },
  { value: 'sqm', label: 'Square Meter' },
  { value: 'lf', label: 'Linear Feet' },
  { value: 'lm', label: 'Linear Meter' },
  { value: 'cy', label: 'Cubic Yard' },
  { value: 'ton', label: 'Ton' },
  { value: 'ls', label: 'Lump Sum' },
];

// Get today's date in ISO format
const getTodayISO = (): string => {
  return new Date().toISOString().split('T')[0];
};

// Get status label and variant
const getStatusInfo = (
  status: string
): { label: string; variant: 'success' | 'warning' | 'error' | 'info' | 'neutral' } => {
  switch (status) {
    case 'approved':
      return { label: 'Approved', variant: 'success' };
    case 'pending_internal':
      return { label: 'Pending Internal', variant: 'warning' };
    case 'pending_client':
      return { label: 'Pending Client', variant: 'warning' };
    case 'negotiating':
      return { label: 'Negotiating', variant: 'info' };
    case 'rejected':
      return { label: 'Rejected', variant: 'error' };
    case 'void':
      return { label: 'Void', variant: 'error' };
    default:
      return { label: 'Draft', variant: 'neutral' };
  }
};

const ChangeOrderDrawer: React.FC<ChangeOrderDrawerProps> = ({
  isOpen,
  onClose,
  changeOrder,
  mode,
  onSuccess,
}) => {
  const projectId = useCurrentProjectId();
  const { success, error: showError } = useToast();
  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';

  // Collapsible sections state
  const [expandedSections, setExpandedSections] = useState({
    lineItems: true,
    markups: true,
    scheduleImpact: true,
    dates: true,
    summary: true,
  });

  // API mutations
  const [createCO, { isLoading: isCreating }] = useCreateChangeOrderMutation();
  const [updateCO, { isLoading: isUpdating }] = useUpdateChangeOrderMutation();
  const isSubmitting = isCreating || isUpdating;

  // Cost codes query
  const { data: costCodesResponse } = useGetCostCodesQuery({ isActive: true, limit: 500 });
  const costCodeOptions = useMemo(() => {
    const costCodes = costCodesResponse?.data?.costCodes || [];
    return costCodes.map((cc: CostCode) => ({
      value: cc.code,
      label: `${cc.code} - ${cc.description}`,
      description: cc.description,
    }));
  }, [costCodesResponse]);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ChangeOrderFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(changeOrderSchema) as any,
    defaultValues: {
      title: '',
      description: '',
      type: 'owner_requested',
      priority: 'medium',
      lineItems: [
        {
          description: '',
          costCode: '',
          quantity: 1,
          unit: 'each',
          unitCost: 0,
          costType: 'material',
        },
      ],
      markups: {
        overhead: 0,
        profit: 0,
        insurance: 0,
        bond: 0,
        contingency: 0,
      },
      taxRate: 0,
      isCredit: false,
      scheduleImpact: {
        hasImpact: false,
        daysAdded: 0,
        description: '',
      },
      requestDate: getTodayISO(),
      requiredByDate: '',
    },
  });

  // Line items field array
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'lineItems',
  });

  // Watch values for calculations
  const watchedLineItems = watch('lineItems');
  const watchedMarkups = watch('markups');
  const watchedTaxRate = watch('taxRate');
  const watchedIsCredit = watch('isCredit');
  const watchedScheduleImpact = watch('scheduleImpact');

  // Calculate totals
  const calculations = useMemo(() => {
    // Calculate costs by type
    const costsByType = {
      labor: 0,
      material: 0,
      equipment: 0,
      subcontractor: 0,
      other: 0,
    };

    watchedLineItems?.forEach(item => {
      const extendedCost = (item?.quantity || 0) * (item?.unitCost || 0);
      const costType = item?.costType || 'other';
      costsByType[costType] += extendedCost;
    });

    const directCost =
      costsByType.labor +
      costsByType.material +
      costsByType.equipment +
      costsByType.subcontractor +
      costsByType.other;

    // Calculate markups
    const overheadAmount = directCost * ((watchedMarkups?.overhead || 0) / 100);
    const profitAmount = directCost * ((watchedMarkups?.profit || 0) / 100);
    // Insurance typically applies only to labor
    const insuranceAmount = costsByType.labor * ((watchedMarkups?.insurance || 0) / 100);
    const bondAmount = directCost * ((watchedMarkups?.bond || 0) / 100);
    const contingencyAmount = directCost * ((watchedMarkups?.contingency || 0) / 100);

    const totalMarkup =
      overheadAmount + profitAmount + insuranceAmount + bondAmount + contingencyAmount;
    const subtotal = directCost + totalMarkup;
    const taxAmount = subtotal * ((watchedTaxRate || 0) / 100);
    const totalAmount = subtotal + taxAmount;

    return {
      costsByType,
      directCost,
      markupAmounts: {
        overhead: overheadAmount,
        profit: profitAmount,
        insurance: insuranceAmount,
        bond: bondAmount,
        contingency: contingencyAmount,
      },
      totalMarkup,
      subtotal,
      taxAmount,
      totalAmount,
    };
  }, [watchedLineItems, watchedMarkups, watchedTaxRate]);

  // Toggle section
  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Reset form when changeOrder changes
  useEffect(() => {
    if (changeOrder && (isEditMode || isViewMode)) {
      reset({
        title: changeOrder.title || '',
        description: changeOrder.description || '',
        type: changeOrder.type || 'owner_requested',
        priority: changeOrder.priority || 'medium',
        lineItems:
          changeOrder.pricing.lineItems.length > 0
            ? changeOrder.pricing.lineItems.map(item => ({
                description: item.description,
                costCode: item.costCode,
                costCodeDescription: item.costCodeDescription,
                quantity: item.quantity,
                unit: item.unit,
                unitCost: item.unitCost,
                costType: item.costType,
              }))
            : [
                {
                  description: '',
                  costCode: '',
                  quantity: 1,
                  unit: 'each',
                  unitCost: 0,
                  costType: 'material' as ChangeOrderCostType,
                },
              ],
        markups: {
          overhead: changeOrder.pricing.markups?.overhead?.percentage || 0,
          profit: changeOrder.pricing.markups?.profit?.percentage || 0,
          insurance: changeOrder.pricing.markups?.insurance?.percentage || 0,
          bond: changeOrder.pricing.markups?.bond?.percentage || 0,
          contingency: changeOrder.pricing.markups?.contingency?.percentage || 0,
        },
        taxRate: changeOrder.pricing.taxRate || 0,
        isCredit: changeOrder.pricing.isCredit || false,
        scheduleImpact: {
          hasImpact: changeOrder.scheduleImpact?.hasImpact || false,
          daysAdded: changeOrder.scheduleImpact?.daysAdded || 0,
          description: changeOrder.scheduleImpact?.description || '',
        },
        requestDate: changeOrder.requestDate?.split('T')[0] || getTodayISO(),
        requiredByDate: changeOrder.requiredByDate?.split('T')[0] || '',
      });
    } else if (mode === 'create') {
      reset({
        title: '',
        description: '',
        type: 'owner_requested',
        priority: 'medium',
        lineItems: [
          {
            description: '',
            costCode: '',
            quantity: 1,
            unit: 'each',
            unitCost: 0,
            costType: 'material',
          },
        ],
        markups: {
          overhead: 10,
          profit: 10,
          insurance: 0,
          bond: 0,
          contingency: 0,
        },
        taxRate: 0,
        isCredit: false,
        scheduleImpact: {
          hasImpact: false,
          daysAdded: 0,
          description: '',
        },
        requestDate: getTodayISO(),
        requiredByDate: '',
      });
    }
  }, [changeOrder, mode, reset, isEditMode, isViewMode]);

  // Handle cost code selection
  const handleCostCodeSelect = (index: number, costCode: string) => {
    setValue(`lineItems.${index}.costCode`, costCode);
    const selectedCC = costCodeOptions.find(cc => cc.value === costCode);
    if (selectedCC) {
      setValue(`lineItems.${index}.costCodeDescription`, selectedCC.description);
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: ChangeOrderFormData) => {
    if (!projectId) {
      showError('No project selected');
      return;
    }

    try {
      if (mode === 'create') {
        const createData: CreateChangeOrderRequest = {
          title: data.title,
          description: data.description,
          type: data.type,
          priority: data.priority,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            costCode: item.costCode,
            costCodeDescription: item.costCodeDescription,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costType: item.costType,
          })),
          markups: {
            overhead: data.markups.overhead || undefined,
            profit: data.markups.profit || undefined,
            insurance: data.markups.insurance || undefined,
            bond: data.markups.bond || undefined,
            contingency: data.markups.contingency || undefined,
          },
          taxRate: data.taxRate || undefined,
          isCredit: data.isCredit,
          scheduleImpact: {
            hasImpact: data.scheduleImpact.hasImpact,
            daysAdded: data.scheduleImpact.hasImpact ? data.scheduleImpact.daysAdded : undefined,
            description: data.scheduleImpact.description || undefined,
          },
          requestDate: data.requestDate,
          requiredByDate: data.requiredByDate || undefined,
        };

        await createCO({ projectId, data: createData }).unwrap();
        success('Change order created successfully');
      } else if (mode === 'edit' && changeOrder) {
        const updateData: UpdateChangeOrderRequest = {
          title: data.title,
          description: data.description,
          type: data.type,
          priority: data.priority,
          lineItems: data.lineItems.map(item => ({
            description: item.description,
            costCode: item.costCode,
            costCodeDescription: item.costCodeDescription,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costType: item.costType,
          })),
          markups: {
            overhead: data.markups.overhead || undefined,
            profit: data.markups.profit || undefined,
            insurance: data.markups.insurance || undefined,
            bond: data.markups.bond || undefined,
            contingency: data.markups.contingency || undefined,
          },
          taxRate: data.taxRate || undefined,
          isCredit: data.isCredit,
          scheduleImpact: {
            hasImpact: data.scheduleImpact.hasImpact,
            daysAdded: data.scheduleImpact.hasImpact ? data.scheduleImpact.daysAdded : undefined,
            description: data.scheduleImpact.description || undefined,
          },
          requiredByDate: data.requiredByDate || undefined,
        };

        await updateCO({
          projectId,
          changeOrderId: changeOrder._id,
          data: updateData,
        }).unwrap();
        success('Change order updated successfully');
      }

      onSuccess?.();
      handleClose();
    } catch (err: unknown) {
      const error = err as {
        data?: { message?: string; errors?: Array<{ field: string; message: string }> };
        message?: string;
      };
      if (error.data?.errors) {
        const errorMessages = error.data.errors.map(e => `${e.field}: ${e.message}`).join(', ');
        showError(errorMessages);
      } else {
        showError(error.data?.message || error.message || 'Failed to save change order');
      }
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const addLineItem = () => {
    append({
      description: '',
      costCode: '',
      quantity: 1,
      unit: 'each',
      unitCost: 0,
      costType: 'material',
    });
  };

  const removeLineItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Drawer - Large size for multiple sections */}
      <div className="fixed top-6 right-6 bottom-6 left-6 z-[100] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white rounded-t-2xl flex-shrink-0">
            <div className="flex items-center space-x-3">
              <FileText className="h-5 w-5 text-brand-600" />
              <div>
                <div className="flex items-center space-x-3">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {mode === 'create'
                      ? 'Create Change Order'
                      : mode === 'edit'
                        ? 'Edit Change Order'
                        : `CO: ${changeOrder?.coNumber}`}
                  </h2>
                  {changeOrder && (
                    <StatusBadge
                      status={getStatusInfo(changeOrder.status).label}
                      variant={getStatusInfo(changeOrder.status).variant}
                      size="sm"
                    />
                  )}
                </div>
                {changeOrder && <p className="text-sm text-gray-500">{changeOrder.title}</p>}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit(handleFormSubmit)} className="flex-1 overflow-y-auto">
            <div className="px-8 py-6 space-y-6">
              {/* Basic Information Section */}
              <section className="space-y-4">
                <div className="flex items-center space-x-2 pb-2 border-b border-gray-200">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                    Basic Information
                  </h3>
                </div>

                <Controller
                  name="title"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label="Title"
                      value={field.value}
                      onChange={e => field.onChange(e.target.value)}
                      error={errors.title?.message}
                      disabled={isViewMode}
                      placeholder="Enter change order title"
                      required
                    />
                  )}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Type"
                        options={TYPE_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.type?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="priority"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        label="Priority"
                        options={PRIORITY_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.priority?.message}
                        disabled={isViewMode}
                        required
                      />
                    )}
                  />
                </div>

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label="Description"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.description?.message}
                      disabled={isViewMode}
                      placeholder="Describe the scope of work and reason for the change"
                      rows={3}
                      required
                    />
                  )}
                />
              </section>

              {/* Line Items Section */}
              <section className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => toggleSection('lineItems')}
                    className="flex items-center space-x-2"
                  >
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Line Items
                    </h3>
                    {expandedSections.lineItems ? (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                  {!isViewMode && expandedSections.lineItems && (
                    <button
                      type="button"
                      onClick={addLineItem}
                      className="btn btn-secondary btn-sm"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Item
                    </button>
                  )}
                </div>

                {/* Line Items Errors */}
                {errors.lineItems &&
                  typeof errors.lineItems === 'object' &&
                  'message' in errors.lineItems && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                      <AlertCircle className="h-4 w-4" />
                      <span>{errors.lineItems.message}</span>
                    </div>
                  )}

                {expandedSections.lineItems && (
                  <div className="space-y-4">
                    {fields.map((field, index) => (
                      <div
                        key={field.id}
                        className="p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900">Item {index + 1}</h4>
                          {!isViewMode && fields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLineItem(index)}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                          <div className="md:col-span-2">
                            <Controller
                              name={`lineItems.${index}.description`}
                              control={control}
                              render={({ field: inputField }) => (
                                <Input
                                  label="Description"
                                  value={inputField.value}
                                  onChange={e => inputField.onChange(e.target.value)}
                                  error={errors.lineItems?.[index]?.description?.message}
                                  disabled={isViewMode}
                                  required
                                  placeholder="Work description"
                                />
                              )}
                            />
                          </div>

                          <Controller
                            name={`lineItems.${index}.costCode`}
                            control={control}
                            render={({ field: inputField }) => (
                              <SearchableDropdown
                                label="Cost Code"
                                options={costCodeOptions}
                                value={inputField.value}
                                onChange={value => handleCostCodeSelect(index, value)}
                                error={errors.lineItems?.[index]?.costCode?.message}
                                disabled={isViewMode}
                                required
                                placeholder="Select..."
                              />
                            )}
                          />

                          <Controller
                            name={`lineItems.${index}.costType`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Dropdown
                                label="Cost Type"
                                options={COST_TYPE_OPTIONS}
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.costType?.message}
                                disabled={isViewMode}
                                required
                              />
                            )}
                          />

                          <Controller
                            name={`lineItems.${index}.quantity`}
                            control={control}
                            render={({ field: inputField }) => (
                              <NumberInput
                                label="Quantity"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.quantity?.message}
                                disabled={isViewMode}
                                required
                                min={0.01}
                                step={0.01}
                              />
                            )}
                          />

                          <Controller
                            name={`lineItems.${index}.unit`}
                            control={control}
                            render={({ field: inputField }) => (
                              <Dropdown
                                label="Unit"
                                options={UNIT_OPTIONS}
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.unit?.message}
                                disabled={isViewMode}
                                required
                              />
                            )}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                          <Controller
                            name={`lineItems.${index}.unitCost`}
                            control={control}
                            render={({ field: inputField }) => (
                              <CurrencyInput
                                label="Unit Cost"
                                value={inputField.value}
                                onChange={inputField.onChange}
                                error={errors.lineItems?.[index]?.unitCost?.message}
                                disabled={isViewMode}
                                required
                              />
                            )}
                          />

                          <div className="flex flex-col justify-end md:col-span-2">
                            <label className="block text-xs text-gray-500 mb-1">
                              Extended Cost
                            </label>
                            <div className="text-lg font-medium text-gray-900">
                              {formatCurrency(
                                (watchedLineItems?.[index]?.quantity || 0) *
                                  (watchedLineItems?.[index]?.unitCost || 0)
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Markups Section */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('markups')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Markups
                    </h3>
                  </div>
                  {expandedSections.markups ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.markups && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <Controller
                        name="markups.overhead"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Overhead (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="markups.profit"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Profit (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="markups.insurance"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Insurance (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="markups.bond"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Bond (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="markups.contingency"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Contingency (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <Controller
                        name="taxRate"
                        control={control}
                        render={({ field }) => (
                          <NumberInput
                            label="Tax Rate (%)"
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isViewMode}
                            min={0}
                            max={100}
                          />
                        )}
                      />

                      <Controller
                        name="isCredit"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-col">
                            <label className="block text-xs text-gray-500 mb-1">
                              Credit/Deduction
                            </label>
                            <label className="inline-flex items-center mt-2">
                              <input
                                type="checkbox"
                                checked={field.value}
                                onChange={e => field.onChange(e.target.checked)}
                                disabled={isViewMode}
                                className="h-4 w-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                              />
                              <span className="ml-2 text-sm text-gray-700">This is a credit</span>
                            </label>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                )}
              </section>

              {/* Schedule Impact Section */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('scheduleImpact')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <AlertTriangle className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Schedule Impact
                    </h3>
                  </div>
                  {expandedSections.scheduleImpact ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.scheduleImpact && (
                  <div className="space-y-4 pt-2">
                    <Controller
                      name="scheduleImpact.hasImpact"
                      control={control}
                      render={({ field }) => (
                        <label className="inline-flex items-center">
                          <input
                            type="checkbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                            disabled={isViewMode}
                            className="h-4 w-4 text-brand-600 rounded border-gray-300 focus:ring-brand-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            This change order has schedule impact
                          </span>
                        </label>
                      )}
                    />

                    {watchedScheduleImpact?.hasImpact && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Controller
                          name="scheduleImpact.daysAdded"
                          control={control}
                          render={({ field }) => (
                            <NumberInput
                              label="Days Added"
                              value={field.value}
                              onChange={field.onChange}
                              error={errors.scheduleImpact?.daysAdded?.message}
                              disabled={isViewMode}
                              min={0}
                              required
                            />
                          )}
                        />

                        <Controller
                          name="scheduleImpact.description"
                          control={control}
                          render={({ field }) => (
                            <Input
                              label="Impact Description"
                              value={field.value}
                              onChange={e => field.onChange(e.target.value)}
                              disabled={isViewMode}
                              placeholder="Describe the schedule impact"
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                )}
              </section>

              {/* Dates Section */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('dates')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Dates
                    </h3>
                  </div>
                  {expandedSections.dates ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.dates && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <Controller
                      name="requestDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Request Date"
                          value={field.value}
                          onChange={date => field.onChange(date || '')}
                          error={errors.requestDate?.message}
                          disabled={isViewMode || isEditMode}
                          required
                        />
                      )}
                    />

                    <Controller
                      name="requiredByDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          label="Required By Date"
                          value={field.value}
                          onChange={date => field.onChange(date || '')}
                          error={errors.requiredByDate?.message}
                          disabled={isViewMode}
                        />
                      )}
                    />
                  </div>
                )}
              </section>

              {/* Financial Summary Section */}
              <section className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleSection('summary')}
                  className="flex items-center justify-between w-full pb-2 border-b border-gray-200"
                >
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-gray-400" />
                    <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">
                      Financial Summary
                    </h3>
                  </div>
                  {expandedSections.summary ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedSections.summary && (
                  <div className="bg-gray-100 rounded-lg p-6 space-y-4">
                    {/* Cost by Type */}
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Cost Breakdown</h4>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <span className="text-xs text-gray-500">Labor</span>
                          <p className="text-sm font-medium">
                            {formatCurrency(calculations.costsByType.labor)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Material</span>
                          <p className="text-sm font-medium">
                            {formatCurrency(calculations.costsByType.material)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Equipment</span>
                          <p className="text-sm font-medium">
                            {formatCurrency(calculations.costsByType.equipment)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Subcontractor</span>
                          <p className="text-sm font-medium">
                            {formatCurrency(calculations.costsByType.subcontractor)}
                          </p>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500">Other</span>
                          <p className="text-sm font-medium">
                            {formatCurrency(calculations.costsByType.other)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t border-gray-300 pt-4 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Direct Cost:</span>
                        <span className="text-gray-900">
                          {formatCurrency(calculations.directCost)}
                        </span>
                      </div>

                      {watchedMarkups?.overhead > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Overhead ({watchedMarkups.overhead}%):
                          </span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.markupAmounts.overhead)}
                          </span>
                        </div>
                      )}
                      {watchedMarkups?.profit > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Profit ({watchedMarkups.profit}%):</span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.markupAmounts.profit)}
                          </span>
                        </div>
                      )}
                      {watchedMarkups?.insurance > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Insurance ({watchedMarkups.insurance}%):
                          </span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.markupAmounts.insurance)}
                          </span>
                        </div>
                      )}
                      {watchedMarkups?.bond > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Bond ({watchedMarkups.bond}%):</span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.markupAmounts.bond)}
                          </span>
                        </div>
                      )}
                      {watchedMarkups?.contingency > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Contingency ({watchedMarkups.contingency}%):
                          </span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.markupAmounts.contingency)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="text-gray-900">
                          {formatCurrency(calculations.subtotal)}
                        </span>
                      </div>

                      {watchedTaxRate > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Tax ({watchedTaxRate}%):</span>
                          <span className="text-gray-900">
                            {formatCurrency(calculations.taxAmount)}
                          </span>
                        </div>
                      )}

                      <div className="flex justify-between text-lg font-semibold border-t border-gray-300 pt-3 mt-3">
                        <span className="text-gray-900">
                          {watchedIsCredit ? 'Credit Amount:' : 'Total Amount:'}
                        </span>
                        <span className={watchedIsCredit ? 'text-green-600' : 'text-brand-600'}>
                          {watchedIsCredit ? '-' : ''}
                          {formatCurrency(calculations.totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            </div>
          </form>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
            <div className="flex justify-between items-center">
              <button type="button" onClick={handleClose} className="btn btn-secondary">
                {isViewMode ? 'Close' : 'Cancel'}
              </button>

              {!isViewMode && (
                <button
                  type="submit"
                  onClick={handleSubmit(handleFormSubmit)}
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin mr-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      </span>
                      {mode === 'create' ? 'Creating...' : 'Saving...'}
                    </>
                  ) : (
                    <>{mode === 'create' ? 'Create Change Order' : 'Save Changes'}</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default ChangeOrderDrawer;
